import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  CX_TICKETS_SIDEBAR_EXPANDED_KEY,
  CX_TICKETS_SIDEBAR_MANUAL_WIDTH_KEY,
} from "@jugl-web/utils/storage";
import {
  ResizableSidebarWrapper,
  useResizableSidebar,
} from "@web-src/components/ResizableSidebar";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useClientSupportPageContext } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useInstagramAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useMemo } from "react";
import { collapsedIconButtonClassName } from "../../consts";
import { useTicketsFilterOptions } from "../../hooks/useTicketsFilterOptions";
import { ReactComponent as ClosePanelIcon } from "./assets/close-panel.svg";
import { ReactComponent as GmailIcon } from "./assets/gmail.svg";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as OpenPanelIcon } from "./assets/open-panel.svg";
import { ReactComponent as SmsIcon } from "./assets/sms.svg";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { NavItem } from "./components/NavItem";
import { SidebarSectionHeader } from "./components/SidebarSectionHeader";

const INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH = 240;
const INBOX_SIDEBAR_MAX_WIDTH = 240;
const INBOX_SIDEBAR_MIN_WIDTH = 80;
const INBOX_SIDEBAR_COLLAPSE_THRESHOLD = 165;

export const InboxSidebar: FC = () => {
  const { entityId } = useEntitySelectedProvider();

  const {
    availableChannels,
    selectedView,
    selectView,
    openCreateTicketSidebar,
  } = useClientSupportPageContext();

  const { openManageAccountDrawer, crmConfig } = useManageAccountsDrawer();

  const { isConnected: isWhatsappConnected } = useWhatsappAccount();
  const { isConnected: isInstagramConnected } = useInstagramAccount();
  const { isConnected: isWebChatConnected } = useWebChatBotAccount();
  const { isConnected: isEmailConnected } = useEmailAccount();
  const { unreadCounts } = useCRMUnreadIndicators();
  const { isCx } = useAppVariant();
  const { options: ticketItems } = useTicketsFilterOptions();
  const { isTicketCreationBlocked } = useTicketsLimit();

  const { isSidebarExpanded, sidebarWidth, minWidth, updateSidebarWidth } =
    useResizableSidebar({
      expandedStorageKey: CX_TICKETS_SIDEBAR_EXPANDED_KEY,
      widthStorageKey: CX_TICKETS_SIDEBAR_MANUAL_WIDTH_KEY,
      defaultExpandedWidth: INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH,
      maxWidth: INBOX_SIDEBAR_MAX_WIDTH,
      minWidth: INBOX_SIDEBAR_MIN_WIDTH,
      collapseThreshold: INBOX_SIDEBAR_COLLAPSE_THRESHOLD,
    });

  const { ticketsApi } = useRestApiProvider();

  const { data: ticketsStats } = ticketsApi.useGetTicketsStatsQuery(
    {
      entityId,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { t } = useTranslations();

  const getChannelIcon = useCallback((channelId: CRMChannel) => {
    switch (channelId) {
      case CRMChannel.whatsapp:
        return <WhatsappIcon className="shrink-0" />;
      case CRMChannel.instagram:
        return <InstagramIcon className="shrink-0" />;
      case CRMChannel.chatbot:
        return <WebChatIcon className="shrink-0" />;
      case CRMChannel.twilio:
        return <SmsIcon className="shrink-0" />;
      case CRMChannel.email:
        return <GmailIcon className="shrink-0" />;
      default:
        return <WebChatIcon className="shrink-0" />;
    }
  }, []);

  const getChannelLabel = useCallback(
    (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return t({
            id: "client-support-page.whatsapp",
            defaultMessage: "WhatsApp",
          });
        case CRMChannel.instagram:
          return t({
            id: "client-support-page.instagram",
            defaultMessage: "Instagram",
          });
        case CRMChannel.chatbot:
          return t({
            id: "client-support-page.web-chat",
            defaultMessage: "Web Chat",
          });
        case CRMChannel.twilio:
          return t({
            id: "client-support-page.twilio",
            defaultMessage: "Text (SMS)",
          });
        case CRMChannel.email:
          return t({
            id: "client-support-page.gmail",
            defaultMessage: "Gmail",
          });
        default:
          return t({
            id: "client-support-page.unknown-channel",
            defaultMessage: "Unknown Channel",
          });
      }
    },
    [t]
  );

  const isChannelConnected = useCallback(
    (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return isWhatsappConnected;
        case CRMChannel.instagram:
          return isInstagramConnected;
        case CRMChannel.chatbot:
          return isWebChatConnected;
        case CRMChannel.twilio:
          return !!crmConfig?.twilio?.id;
        case CRMChannel.email:
          return isEmailConnected;
        default:
          return true;
      }
    },
    [
      isWhatsappConnected,
      isInstagramConnected,
      isWebChatConnected,
      isEmailConnected,
      crmConfig?.twilio?.id,
    ]
  );

  const getUnreadCountForChannel = useCallback(
    (channel: CRMChannel): number => {
      if (!unreadCounts) {
        return 0;
      }

      switch (channel) {
        case CRMChannel.whatsapp:
          return unreadCounts.wa_unread ?? 0;
        case CRMChannel.instagram:
          return unreadCounts.ig_unread ?? 0;
        case CRMChannel.chatbot:
          return unreadCounts.chatbot_unread ?? 0;
        case CRMChannel.twilio:
          return unreadCounts.twilio_unread ?? 0;
        case CRMChannel.email:
          return unreadCounts.email_unread ?? 0;
        default:
          return 0;
      }
    },
    [unreadCounts]
  );

  const channelItems = useMemo(
    () =>
      availableChannels.map((channel) => ({
        id: channel.id,
        label: channel.name || getChannelLabel(channel.id),
        icon: getChannelIcon(channel.id),
        count: getUnreadCountForChannel(channel.id),
        isDisconnected: !isChannelConnected(channel.id),
      })),
    [
      availableChannels,
      getChannelIcon,
      getChannelLabel,
      getUnreadCountForChannel,
      isChannelConnected,
    ]
  );

  const handleToggleSidebar = () => {
    updateSidebarWidth(
      isSidebarExpanded ? minWidth : INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH
    );
  };

  const hasAllChannelsConnected =
    availableChannels.length >= Object.keys(CRMChannel).length;

  return (
    <ResizableSidebarWrapper
      sidebarWidth={sidebarWidth}
      onWidthChange={updateSidebarWidth}
    >
      {(isResizing) => (
        <div
          className={cx(
            "flex h-full shrink-0 flex-col border-0 border-r border-solid border-dark-100 bg-white transition-[width] duration-300",
            isResizing && "transition-none"
          )}
          style={{ width: `${sidebarWidth}px` }}
        >
          <div
            className={cx(
              "flex h-[60px] shrink-0 items-center gap-3 border-0 border-b border-solid border-dark-100 px-6",
              !isSidebarExpanded && "justify-center px-0"
            )}
          >
            {isSidebarExpanded && (
              <span className="truncate font-secondary text-lg font-medium text-dark">
                {t({
                  id: "client-support-page.inbox-sidebar.inbox",
                  defaultMessage: "Inbox",
                })}
              </span>
            )}
            <Tooltip
              placement="right"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  onClick={handleToggleSidebar}
                  className={cx(
                    collapsedIconButtonClassName,
                    "h-8 w-8",
                    isSidebarExpanded && "ml-auto"
                  )}
                >
                  {isSidebarExpanded ? <ClosePanelIcon /> : <OpenPanelIcon />}
                </PlainButton>
              )}
            >
              {isSidebarExpanded
                ? t({
                    id: "client-support-page.inbox-sidebar.collapse-menu",
                    defaultMessage: "Collapse Menu",
                  })
                : t({
                    id: "client-support-page.inbox-sidebar.expand-menu",
                    defaultMessage: "Expand Menu",
                  })}
            </Tooltip>
          </div>
          <div className="jugl__custom-scrollbar flex flex-col overflow-x-hidden overflow-y-auto py-6">
            <div className="flex flex-col gap-3 px-4">
              <SidebarSectionHeader
                title={t({
                  id: "client-support-page.inbox-sidebar.channels",
                  defaultMessage: "Channels",
                })}
                isExpanded={isSidebarExpanded}
                showAddButton={!hasAllChannelsConnected}
                addButtonTooltip={t({
                  id: "client-support-page.inbox-sidebar.connect-new-channel",
                  defaultMessage: "Connect new Channel",
                })}
                onAdd={() =>
                  openManageAccountDrawer({ name: "connectedAccountList" })
                }
              />
              {channelItems.map((item) => (
                <NavItem
                  key={item.id}
                  label={item.label}
                  icon={item.icon}
                  isActive={
                    selectedView.kind === "channel" &&
                    selectedView.channel === item.id
                  }
                  isExpanded={isSidebarExpanded}
                  isDisconnected={item.isDisconnected}
                  count={item.count}
                  onClick={() =>
                    selectView({ kind: "channel", channel: item.id })
                  }
                />
              ))}
            </div>

            {isCx && (
              <>
                <div className="my-5 h-px w-full bg-dark-100" />

                <div className="flex flex-col gap-3 px-4">
                  <SidebarSectionHeader
                    title={t({
                      id: "client-support-page.inbox-sidebar.tickets",
                      defaultMessage: "Tickets",
                    })}
                    isExpanded={isSidebarExpanded}
                    addButtonTooltip={t({
                      id: "client-support-page.inbox-sidebar.create-ticket",
                      defaultMessage: "Create Ticket",
                    })}
                    isAddDisabled={isTicketCreationBlocked}
                    renderDisabledTooltip={(renderTrigger) => (
                      <TicketCreationBlockedTooltip
                        renderTrigger={renderTrigger}
                      />
                    )}
                    onAdd={() => openCreateTicketSidebar()}
                  />
                  {ticketItems.map((item) => (
                    <NavItem
                      key={item.id}
                      label={item.label}
                      icon={item.icon}
                      isActive={
                        selectedView.kind === "tickets" &&
                        selectedView.filter === item.id
                      }
                      isExpanded={isSidebarExpanded}
                      count={ticketsStats?.[item.id] ?? 0}
                      onClick={() =>
                        selectView({ kind: "tickets", filter: item.id })
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ResizableSidebarWrapper>
  );
};
